import React, { useState, useEffect } from "react";
import _ from "lodash";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { Input, Button, InputSelect, TextArea } from "../../../../form";
import ToggleComponent from "../../../../common/toggleComponent";
import { FullScreenLoader } from "../../../../common";
import ClassProjectSetup from "./classProjectsSetup";

import {
  getRegions,
  getTeachersPortal,
  updateTeachersPortal,
  getSystemAdminUserGroups,
  getProfessionMaps,
  getProfessionsAccessibleToOptions,
  getEmailTemplates,
  getRegionalEvents
} from "../../../../../service";

import { ArrowBackIcon, editIconPencil, PlusIcon, MinusSquare } from "../../../../../asssets/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { StarIcon } from "@heroicons/react/24/outline";
import { FaSave } from "react-icons/fa";

const registrationFormInput = [
  { label: "First Name", id: "firstName", enabled: true, allowDisable: false },
  { label: "Last Name", id: "lastName", enabled: true, allowDisable: true },
  { label: "Type of establishment/ organisation", id: "type", enabled: false, allowDisable: true },
  { label: "Name of establishment/ organisation", id: "name", enabled: false, allowDisable: true },
  {
    label: "Address of establishment/ organisation",
    id: "address",
    enabled: false,
    allowDisable: true
  },
  { label: "City of establishment/ organisation", id: "city", enabled: false, allowDisable: true },
  {
    label: "Postal Code of establishment/ organisation",
    id: "postalCode",
    enabled: false,
    allowDisable: true
  },
  {
    label: "Region of establishment/ organisation",
    id: "region",
    enabled: false,
    allowDisable: true
  },
  {
    label: "Academy of establishment/ organisation",
    id: "academy",
    enabled: false,
    allowDisable: true
  },
  { label: "School/ Organisation", id: "schoolOrganization", enabled: false, allowDisable: true },
  {
    label: "Function/ Your role in your school/ Organisation",
    id: "functionTask",
    enabled: false,
    allowDisable: true
  },
  { label: "Email", id: "email", enabled: true, allowDisable: true },
  { label: "Region", id: "canton", enabled: true, allowDisable: true },
  { label: "Telephone", id: "phone", enabled: true, allowDisable: true },
  { label: "Language", id: "language", enabled: true, allowDisable: true },
  { label: "Is Teacher?", id: "isTeacher", enabled: true, allowDisable: true },
  { label: "Want Portal Access?", id: "wantAccess", enabled: true, allowDisable: true },
  { label: "Gender", id: "gender", enabled: false, allowDisable: true }
];

const classServicesDefault = {
  created: { enabled: false, label: "Date Created", order: 0 },
  participants: { enabled: true, label: "Participants", order: 1 },
  studentsListWithDeleteOption: {
    enabled: false,
    label: "Student list with delete option",
    order: 2
  },
  downloadAccessCodes: { enabled: true, label: "Download Access Codes", order: 3 },
  downloadAccessCodesPdf: { enabled: false, label: "Download Access Codes PDF", order: 4 },
  downloadUsedAccessCodes: { enabled: true, label: "Download Used Access Codes", order: 5 },
  vipTestLink: { enabled: true, label: "VIP Test Link", order: 6 },
  parentVipTestLink: { enabled: false, label: "Parent VIP Test Link", order: 7 },
  classWebpage: { enabled: true, label: "Class Webpage", order: 8 },
  downloadVipProfiles: { enabled: true, label: "Download VIP Profiles", order: 9 },
  downloadStrengthProfiles: { enabled: true, label: "Download Strength Profiles", order: 10 },
  downloadProfessionalRoleProfiles: {
    enabled: true,
    label: "Download Professional Role Profiles",
    order: 11
  },
  downloadStudentProfiles: { enabled: true, label: "Download Student Profiles", order: 12 },
  projectFiles: { enabled: false, label: "Project Files", order: 13 },
  profileConverter: { enabled: true, label: "Profile Converter", order: 14 },
  transferStudent: { enabled: false, label: "Transfer Student", order: 15 },
  delete: { enabled: true, label: "Delete Class", order: 16 },
  vimeoAndPdfDownload: { enabled: false, label: "Vimeo and PDF Download", order: 17 }
};

const teachersPortalFeaturesDefault = {
  downloadsLinkLocation: { sidebarDefault: false, sidebarBottom: false },
  showNumberOfParticipants: false,
  enableHistoryTab: false,
  historyTabDate: "", // Date picker input
  portalTheme: {
    projectsTableFirstColumnTextColor: "#000",
    projectsTableHeadersTextColor: "#000"
  }
};

const classDownloadsConfigurationDefault = {
  pdfTemplateNamesAccessCodes: "",
  profilePdfDownloadsConfigVipTitle: "",
  profilePdfDownloadsConfigStrengthTitle: "",
  profilePdfDownloadsConfigStrengthTpl: "",
  profilePdfDownloadsConfigRolesTitle: "",
  profilePdfDownloadsConfigRolesTpl: ""
};

const studentPortalSetupDefault = {
  downloadStudentProfiles: { enabled: false, label: "Download Student PDF at end" },
  editNameInPortal: { enabled: false, label: "Allow editing name" },
  communicationPreferencesStep: { enabled: false, label: "Communication Preferences Step" }
};

const adminPanelTabsDefault = {
  default: [
    { label: "Welcome (Tab)", id: "welcome", enabled: true, allowDisable: true },
    {
      label: "Introduction & Instructions (Tab)",
      id: "guide",
      enabled: true,
      allowDisable: true,
      buttonLinks: [
        { title: { en: "EN Title", de: "", fr: "", it: "" }, link: "https://google.com" },
        { title: { en: "EN Title", de: "", fr: "", it: "" }, link: "https://google.com" }
      ]
    },
    { label: "Create New Project (Tab)", id: "create-project", enabled: true, allowDisable: true },
    { label: "Manage Projects (Tab)", id: "projects", enabled: true, allowDisable: true },
    { label: "Downloads (Tab)", id: "downloads", enabled: true, allowDisable: true },
    { label: "Contact (Tab)", id: "contact", enabled: true, allowDisable: true },
    { label: "About Us (Tab)", id: "about-us", enabled: true, allowDisable: true },
    {
      label: "Mission Future Class (Tab)",
      id: "mission-future-class",
      enabled: true,
      allowDisable: true
    },
    {
      label: "Mission Future Event (Tab)",
      id: "mission-future-event",
      enabled: true,
      allowDisable: true
    },
    {
      label: "Book a Profile Type",
      id: "book-profile-type",
      enabled: false,
      allowDisable: true,
      subOptions: [
        {
          label: "Date & Time",
          id: "date-time",
          enabled: false,
          allowDisable: true
        },
        {
          label: "Location",
          id: "location",
          enabled: false,
          allowDisable: true
        },
        {
          label: "Profile/Profession Name",
          id: "profile-profession-name",
          enabled: false,
          allowDisable: true
        },
        {
          label: "Comments",
          id: "comments",
          enabled: false,
          allowDisable: true
        },
        {
          label: "Reciever address (who request will be sent to)",
          placeholder: "email@gmail.com",
          id: "reciever-address",
          type: "text-input",
          value: ""
        }
      ]
    }
  ]
};

const classProjectTypesDefault = {
  default: {
    title: { en: "Default Project", de: "", fr: "", it: "" },
    professionMap: "",
    enableClassLoginButton: false,
    enableStudentLoginButton: false,
    enableParentLoginButton: false,
    classPage: [
      {
        id: "studentsList",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "strengthWordCloud",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "professionRolesImageCloud",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "profileVideoSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        customVideoOptions: []
      },
      {
        id: "professionSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        professionType: ""
      },
      {
        id: "downloadAllProfessions",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "aptitudeWordCloud",
        aptitudeId: uuidv4(),
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        aptitudeCloudOptions: [],
        useOrganizationStrengthsList: false,
        useOrganizationRolesList: false,
        useOrganizationProfessionsList: false,
        professionType: "",
        showAsList: false,
        listLength: 4,
        replacePreviousSelections: false
      },
      {
        id: "regionalLinks",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedRegions: []
      },
      {
        id: "htmlBox",
        html: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "videosPlayer",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        enableAttitudeCloud: false,
        aptitudeId: uuidv4(),
        videos: [{ title: { en: "", de: "", fr: "", it: "" }, url: "" }]
      },
      {
        id: "classCommunicationPreferences",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "classMapSelections",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        sf_img: { en: "", de: "", fr: "", it: "" },
        sf_icons_img: { en: "", de: "", fr: "", it: "" },
        st_img: { en: "", de: "", fr: "", it: "" },
        st_icons_img: { en: "", de: "", fr: "", it: "" },
        nf_img: { en: "", de: "", fr: "", it: "" },
        nf_icons_img: { en: "", de: "", fr: "", it: "" },
        nt_img: { en: "", de: "", fr: "", it: "" },
        nt_icons_img: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "teamProfilePage",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "studentRandomInputsWordCloud",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        studentRandomInputsWordCloudId: ""
      },
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ],
    studentPage: [
      {
        id: "preferredStrengths",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "preferredProfessionalRoles",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "similarProfileBadges",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        studentAttributesToMatch: [],
        regionalEvents: [],
        navigationPopup: {
          enabled: false,
          header: { en: "", de: "", fr: "", it: "" },
          subtitle: { en: "", de: "", fr: "", it: "" },
          confirmButtonText: { en: "", de: "", fr: "", it: "" },
          cancelButtonText: { en: "", de: "", fr: "", it: "" }
        }
      },
      {
        id: "professionSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        professionType: ""
      },
      {
        id: "profileDownload",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        downloadType: "default"
      },
      {
        id: "profileLinkCopy",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "htmlBox",
        html: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "videosPlayer",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        enableAttitudeCloud: false,
        aptitudeId: uuidv4(),
        videos: [{ title: { en: "", de: "", fr: "", it: "" }, url: "" }]
      },
      {
        id: "studentCommunicationPreferences",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "studentRandomInputs",
        numberOfInputs: 3,
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        popupHeading: { en: "", de: "", fr: "", it: "" },
        popupSubmitButtonText: { en: "", de: "", fr: "", it: "" },
        popupInputPlaceholder: { en: "", de: "", fr: "", it: "" },
        options: [{ en: "", de: "", fr: "", it: "" }],
        studentRandomInputsWordCloudId: ""
      },
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ],
    parentPage: [
      {
        id: "preferredStrengths",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "preferredProfessionalRoles",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "similarProfileBadges",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        studentAttributesToMatch: [],
        regionalEvents: [],
        navigationPopup: {
          enabled: false,
          header: { en: "", de: "", fr: "", it: "" },
          subtitle: { en: "", de: "", fr: "", it: "" },
          confirmButtonText: { en: "", de: "", fr: "", it: "" },
          cancelButtonText: { en: "", de: "", fr: "", it: "" }
        }
      },
      {
        id: "professionSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        professionType: ""
      },
      {
        id: "profileDownload",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        downloadType: "default"
      },
      {
        id: "profileLinkCopy",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "htmlBox",
        html: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "videosPlayer",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        enableAttitudeCloud: false,
        aptitudeId: uuidv4(),
        videos: [{ title: { en: "", de: "", fr: "", it: "" }, url: "" }]
      },
      {
        id: "studentCommunicationPreferences",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "studentRandomInputs",
        numberOfInputs: 3,
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        popupHeading: { en: "", de: "", fr: "", it: "" },
        popupSubmitButtonText: { en: "", de: "", fr: "", it: "" },
        popupInputPlaceholder: { en: "", de: "", fr: "", it: "" },
        options: [{ en: "", de: "", fr: "", it: "" }],
        studentRandomInputsWordCloudId: ""
      },
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ],
    teamProfilePage: [
      {
        id: "studentsList",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "strengthWordCloud",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "professionRolesImageCloud",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        professionRolesImageCloudId: uuidv4()
      },
      {
        id: "profileVideoSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        customVideoOptions: []
      },
      {
        id: "professionSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        professionType: ""
      },
      {
        id: "downloadAllProfessions",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "aptitudeWordCloud",
        aptitudeId: uuidv4(),
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        aptitudeCloudOptions: [],
        useOrganizationStrengthsList: false,
        useOrganizationRolesList: false,
        useOrganizationProfessionsList: false,
        professionType: "",
        showAsList: false,
        listLength: 4,
        replacePreviousSelections: false
      },
      {
        id: "regionalLinks",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedRegions: []
      },
      {
        id: "htmlBox",
        html: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "videosPlayer",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        enableAttitudeCloud: false,
        aptitudeId: uuidv4(),
        videos: [{ title: { en: "", de: "", fr: "", it: "" }, url: "" }]
      },
      {
        id: "classCommunicationPreferences",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "teamOrganizations",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "singleQuestionBox",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        singleQuestionBoxId: uuidv4()
      },
      {
        id: "dataTable",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        columns: [{ en: "", de: "", fr: "", it: "" }],
        dataTableId: uuidv4(),
        subInputField: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "attitudesComponent",
        title: { en: "", de: "", fr: "", it: "" },
        subtitle: { en: "", de: "", fr: "", it: "" },
        titleNumber2: { en: "", de: "", fr: "", it: "" },
        subtitleNumber2: { en: "", de: "", fr: "", it: "" },
        attitudesList: [
          {
            title: { en: "", de: "", fr: "", it: "" },
            description: { en: "", de: "", fr: "", it: "" }
          }
        ],
        submitButtonText: { en: "", de: "", fr: "", it: "" },
        attitudesComponentId: uuidv4()
      },
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ]
  }
};

const externalProjectTypesDefault = {
  default: {
    projectId: uuidv4(),
    vipClassPageEnabled: false,
    vipClassDemoTanIds: [],
    title: { en: "Default Project", de: "", fr: "", it: "" },
    sendEmailAfterCompletion: false,
    emailTemplateId: "",
    enableClassLoginButton: false,
    enableStudentLoginButton: false,
    enableParentLoginButton: false,
    studentPage: [
      {
        id: "preferredStrengths",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "preferredProfessionalRoles",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "similarProfileBadges",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        studentAttributesToMatch: [],
        regionalEvents: [],
        navigationPopup: {
          enabled: false,
          header: { en: "", de: "", fr: "", it: "" },
          subtitle: { en: "", de: "", fr: "", it: "" },
          confirmButtonText: { en: "", de: "", fr: "", it: "" },
          cancelButtonText: { en: "", de: "", fr: "", it: "" }
        }
      },
      {
        id: "professionSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        professionType: ""
      },
      {
        id: "profileDownload",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        downloadType: "default"
      },
      {
        id: "htmlBox",
        html: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "videosPlayer",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        enableAttitudeCloud: false,
        aptitudeId: uuidv4(),
        videos: [{ title: { en: "", de: "", fr: "", it: "" }, url: "" }]
      },
      {
        id: "studentCommunicationPreferences",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ],
    classPage: [
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ],
    parentPage: [
      {
        id: "preferredStrengths",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        downloadType: "default"
      },
      {
        id: "preferredProfessionalRoles",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "similarProfileBadges",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        studentAttributesToMatch: [],
        regionalEvents: [],
        navigationPopup: {
          enabled: false,
          header: { en: "", de: "", fr: "", it: "" },
          subtitle: { en: "", de: "", fr: "", it: "" },
          confirmButtonText: { en: "", de: "", fr: "", it: "" },
          cancelButtonText: { en: "", de: "", fr: "", it: "" }
        }
      },
      {
        id: "professionSelection",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        selectedUsersGroups: [],
        professionType: ""
      },
      {
        id: "profileDownload",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "htmlBox",
        html: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "videosPlayer",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" },
        enableAttitudeCloud: false,
        aptitudeId: uuidv4(),
        videos: [{ title: { en: "", de: "", fr: "", it: "" }, url: "" }]
      },
      {
        id: "studentCommunicationPreferences",
        foregroundHeading: { en: "", de: "", fr: "", it: "" },
        backgroundHeading: { en: "", de: "", fr: "", it: "" },
        subText: { en: "", de: "", fr: "", it: "" },
        buttonText: { en: "", de: "", fr: "", it: "" }
      },
      {
        id: "infoPopup",
        heading: { en: "", de: "", fr: "", it: "" },
        subHeading: { en: "", de: "", fr: "", it: "" },
        headingColor: "",
        subHeadingColor: "",
        backgroundColor: "",
        closeIconBackgroundColor: "",
        closeIconColor: "",
        buttonText: { en: "", de: "", fr: "", it: "" },
        buttonBackgroundColor: "",
        buttonTextColor: "",
        autoShowPopup: false
      }
    ]
  }
};

const TeacherAdminSetup = ({ title, org, currentService, setShowComponent }) => {
  const [maintenanceMode, setMaintenanceMode] = useState({
    enabled: false,
    showLogo: false,
    html: ""
  });
  const [vipTestLink, setVipTestLink] = useState("");
  const [serverBaseUrl, setServerBaseUrl] = useState("");
  const [registrationPath, setRegistrationPath] = useState("");
  const [productId, setProductId] = useState("");
  const [enableModerators, setEnableModerators] = useState(false);
  const [parentsVIPProductId, setParentsVIPProductId] = useState("");
  const [registrationForm, setRegistrationForm] = useState([...registrationFormInput]);
  const [classServices, setClassServices] = useState({ ...classServicesDefault });
  const [classDownloadsConfig, setClassDownloadsConfig] = useState({
    ...classDownloadsConfigurationDefault
  });
  const [studentPortalSetup, setStudentPortalSetup] = useState({ ...studentPortalSetupDefault });
  const [teachersPortalFeatures, setTeachersPortalFeatures] = useState({
    ...teachersPortalFeaturesDefault
  });
  const [adminPanelTabs, setAdminPanelTabs] = useState({ ...adminPanelTabsDefault });
  const [classProjectTypes, setClassProjectTypes] = useState({ ...classProjectTypesDefault });
  const [regionalProjectTypes, setRegionalProjectTypes] = useState({});
  const [externalProjectTypes, setExternalProjectTypes] = useState({
    ...externalProjectTypesDefault
  });
  const [regions, setRegions] = useState([]);
  const [duplicatedRegions, setDuplicatedRegions] = useState([]);
  const [systemAdminGroups, setSystemAdminGroups] = useState([]);
  const [professionTypes, setProfessionTypes] = useState([]);
  const [professionMaps, setProfessionMaps] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [regionalEvents, setRegionalEvents] = useState([]);
  const [displayToggle, setDisplayToggle] = useState({});
  const [loading, setLoading] = useState(false);

  const toggleMaintenanceMode = () =>
    setMaintenanceMode({ ...maintenanceMode, enabled: !maintenanceMode?.enabled });

  const toggleMaintenanceLogo = () =>
    setMaintenanceMode({ ...maintenanceMode, showLogo: !maintenanceMode?.showLogo });

  const updateMaintenceModeHTML = (updatedHTML) =>
    setMaintenanceMode({ ...maintenanceMode, html: updatedHTML });

  const toggleEnableFormField = (index) => {
    const temp = [...registrationForm];
    temp[index].enabled = !temp[index].enabled;
    setRegistrationForm(temp);
  };

  const toggleRequiredFormField = (index) => {
    const temp = [...registrationForm];
    temp[index].required = !temp[index].required;
    setRegistrationForm(temp);
  };

  const toggleEnableClassService = (service) => {
    const temp = { ...classServices };
    temp[service].enabled = !temp[service].enabled;
    setClassServices(temp);
  };

  const toggleEnableClassServiceForHistoryTab = (service) => {
    const temp = { ...classServices };
    temp[service].enabledForHistoryTab = !temp[service].enabledForHistoryTab;
    setClassServices(temp);
  };

  const onDragEndClassService = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    const sortedKeys = Object.keys(classServices).sort(
      (a, b) => classServices[a].order - classServices[b].order
    );
    const [movedKey] = sortedKeys.splice(sourceIndex, 1);
    sortedKeys.splice(destIndex, 0, movedKey);

    const temp = { ...classServices };
    sortedKeys.forEach((key, index) => {
      temp[key] = { ...temp[key], order: index };
    });
    setClassServices(temp);
  };

  const toggleStudentPortalService = (service) => {
    const temp = { ...studentPortalSetup };
    temp[service].enabled = !temp[service].enabled;
    setStudentPortalSetup(temp);
  };

  const updateTeachersPortalFeatures = (key, value, subKey, reset = false) => {
    const temp = { ...teachersPortalFeatures };
    if (subKey) {
      if (reset) {
        Object.keys(temp[key]).forEach((k) => (temp[key][k] = false));
      }
      temp[key][subKey] = value;
    } else {
      temp[key] = value;
    }
    setTeachersPortalFeatures(temp);
  };

  const toggleAdminPanelTabs = (index, key, subIndex) => {
    const temp = { ...adminPanelTabs };
    if (subIndex !== undefined) {
      temp[key][index].subOptions[subIndex].enabled =
        !temp[key][index].subOptions[subIndex].enabled;
    } else {
      temp[key][index].enabled = !temp[key][index].enabled;
    }
    setAdminPanelTabs(temp);
  };

  const addNewButtonLink = (index, key) => {
    const temp = { ...adminPanelTabs };
    temp[key][index].buttonLinks.push({ title: { en: "", de: "", fr: "", it: "" }, link: "" });
    setAdminPanelTabs(temp);
  };

  const deleteButtonLink = (index, key, buttonIndex) => {
    const temp = { ...adminPanelTabs };
    temp[key][index].buttonLinks.splice(buttonIndex, 1);
    setAdminPanelTabs(temp);
  };

  const updateButtonLinkInfo = (index, key, buttonIndex, newData) => {
    const temp = { ...adminPanelTabs };
    temp[key][index].buttonLinks[buttonIndex] = { ...newData };
    setAdminPanelTabs(temp);
  };

  const updateTextInputValue = (index, key, subIndex, value) => {
    const temp = { ...adminPanelTabs };
    if (subIndex !== undefined) {
      temp[key][index].subOptions[subIndex].value = value;
    } else {
      temp[key][index].value = value;
    }
    setAdminPanelTabs(temp);
  };

  const duplicateForRegion = (duplicateKey = "default", newRegionKey = "", deletePrev = false) => {
    if (duplicatedRegions.length === regions.length) {
      return toast.error("All regions are already duplicated");
    }

    const temp = _.cloneDeep({ ...adminPanelTabs });
    const toCopy = _.cloneDeep([...temp[duplicateKey]]);

    // Fetch next avaialble region
    let nextRegion = regions.find((region) => !duplicatedRegions.includes(region._id));
    if (!nextRegion) return;

    if (newRegionKey) {
      nextRegion = regions.find((region) => region._id === newRegionKey);
    }

    // Set copy
    temp[nextRegion._id] = toCopy;
    setAdminPanelTabs(temp);
    setDuplicatedRegions([...duplicatedRegions, nextRegion._id]);

    // If delete
    if (deletePrev) {
      delete temp[duplicateKey];
      // remove from duplicated regions
      const tempDuplicatedRegions = [...duplicatedRegions];
      const index = tempDuplicatedRegions.indexOf(duplicateKey);
      if (index > -1) {
        tempDuplicatedRegions.splice(index, 1);
        setDuplicatedRegions(tempDuplicatedRegions);
      }

      setAdminPanelTabs(temp);
    }
  };

  const deleteRegion = (key) => {
    const temp = _.cloneDeep({ ...adminPanelTabs });
    const toDelete = temp[key];

    if (toDelete) delete temp[key];

    setAdminPanelTabs(temp);

    const tempDuplicatedRegions = [...duplicatedRegions];
    const index = tempDuplicatedRegions.indexOf(key);
    if (index > -1) {
      tempDuplicatedRegions.splice(index, 1);
      setDuplicatedRegions(tempDuplicatedRegions);
    }
  };

  const updateClassProjectTitle = (variant, { en, de, fr, it }, region, externalProjectType) => {
    if (externalProjectType) {
      const temp = { ...externalProjectTypes };
      temp[variant].title = { en, de, fr, it };
      setExternalProjectTypes(temp);
    } else if (!region || region === "default") {
      const temp = { ...classProjectTypes };
      temp[variant].title = { en, de, fr, it };
      setClassProjectTypes(temp);
    } else {
      const temp = { ...regionalProjectTypes };
      temp[region][variant].title = { en, de, fr, it };
      setRegionalProjectTypes(temp);
    }
  };

  const selectProjectTypeRegionalLink = (
    type,
    variant,
    index,
    regionId,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    const selectedRegions = temp[variant]?.[type]?.[index].selectedRegions;
    if (selectedRegions.includes(regionId)) {
      const indexToRemove = selectedRegions.indexOf(regionId);
      selectedRegions.splice(indexToRemove, 1);
    } else {
      selectedRegions.push(regionId);
    }

    temp[variant][type][index].selectedRegions = [...selectedRegions];

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateBackgroundHeading = (
    type,
    variant,
    index,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].backgroundHeading) {
      temp[variant][type][index].backgroundHeading = {
        en: "",
        de: "",
        fr: "",
        it: ""
      };
    }
    temp[variant][type][index].backgroundHeading[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateForegroundHeading = (
    type,
    variant,
    index,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].foregroundHeading) {
      temp[variant][type][index].foregroundHeading = {
        en: "",
        de: "",
        fr: "",
        it: ""
      };
    }
    temp[variant][type][index].foregroundHeading[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateSubText = (type, variant, index, lang, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].subText) {
      temp[variant][type][index].subText = {
        en: "",
        de: "",
        fr: "",
        it: ""
      };
    }
    temp[variant][type][index].subText[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateButtonText = (type, variant, index, lang, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].buttonText) {
      temp[variant][type][index].buttonText = {
        en: "",
        de: "",
        fr: "",
        it: ""
      };
    }
    temp[variant][type][index].buttonText[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateDownloadType = (type, variant, index, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].downloadType = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateProjectFor = (variant, index, id, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant].projectFor = id;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateHTMLBox = (type, variant, index, lang, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].html) {
      temp[variant][type][index].html = {
        en: "",
        de: "",
        fr: "",
        it: ""
      };
    }
    temp[variant][type][index].html[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateInfoPopupField = (
    type,
    variant,
    index,
    field,
    value,
    region,
    externalProjectType,
    lang = null
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    const row = temp[variant][type][index];
    if (field === "heading" || field === "subHeading") {
      if (!row[field]) row[field] = { en: "", de: "", fr: "", it: "" };
      row[field][lang] = value;
    } else {
      row[field] = value;
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateVideosPlayerTitle = (
    type,
    variant,
    index,
    videoIndex,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].videos[videoIndex].title) {
      temp[variant][type][index].videos[videoIndex].title = {
        en: "",
        de: "",
        fr: "",
        it: ""
      };
    }
    temp[variant][type][index].videos[videoIndex].title[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateVideosPlayerUrl = (
    type,
    variant,
    index,
    videoIndex,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].videos[videoIndex].url = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const addNewVideoInVideosPlayer = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].videos.push({ title: { en: "", de: "", fr: "", it: "" }, url: "" });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const deleteVideoInVideosPlayer = (
    type,
    variant,
    index,
    videoIndex,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].videos.splice(videoIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleVideoPlayerAptitudeCloud = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].enableAttitudeCloud =
      !temp[variant][type][index].enableAttitudeCloud;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const selectUsersGroup = (type, variant, index, groupId, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant]?.[type]?.[index]?.selectedUsersGroups) {
      temp[variant][type][index].selectedUsersGroups = [];
    }
    const selectedUsersGroups = temp[variant]?.[type]?.[index]?.selectedUsersGroups;

    if (selectedUsersGroups.includes(groupId)) {
      const indexToRemove = selectedUsersGroups.indexOf(groupId);
      selectedUsersGroups.splice(indexToRemove, 1);
    } else {
      selectedUsersGroups.push(groupId);
    }

    const systemGroupIds = systemAdminGroups
      ?.map((sag) => sag?._id)
      .filter((id) => id !== undefined);

    // Ensuring that only real system group ids get inserted
    temp[variant][type][index].selectedUsersGroups = [...selectedUsersGroups].filter((groupId) =>
      systemGroupIds?.includes(groupId)
    );

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const discoverChampionCustomUrl = (type, variant, index, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].discoverChampionCustomUrl = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateTotalBadgesToShow = (type, variant, index, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].totalBadgesToShow = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const selectStudentAttributesToMatch = (
    type,
    variant,
    index,
    attributeId,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant]?.[type]?.[index]?.studentAttributesToMatch) {
      temp[variant][type][index].studentAttributesToMatch = [];
    }

    const studentAttributesToMatch = temp[variant][type][index].studentAttributesToMatch;

    if (studentAttributesToMatch.includes(attributeId)) {
      const indexToRemove = studentAttributesToMatch.indexOf(attributeId);
      studentAttributesToMatch.splice(indexToRemove, 1);
    } else {
      studentAttributesToMatch.push(attributeId);
    }

    temp[variant][type][index].studentAttributesToMatch = [...studentAttributesToMatch];

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const setRegionalEventProfileType = (
    type,
    variant,
    index,
    profileTypeId,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant]?.[type]?.[index]) {
      return;
    }

    temp[variant][type][index].regionalEventProfileTypeId = profileTypeId;
    // Clear selected regional events when profile type changes
    temp[variant][type][index].regionalEvents = [];

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const selectRegionalEvent = (type, variant, index, eventId, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant]?.[type]?.[index]?.regionalEvents) {
      temp[variant][type][index].regionalEvents = [];
    }

    const regionalEvents = temp[variant][type][index].regionalEvents;

    if (regionalEvents.includes(eventId)) {
      const indexToRemove = regionalEvents.indexOf(eventId);
      regionalEvents.splice(indexToRemove, 1);
    } else {
      regionalEvents.push(eventId);
    }

    temp[variant][type][index].regionalEvents = [...regionalEvents];

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateSimilarProfileBadgesNavigationPopupField = (
    type,
    variant,
    index,
    field,
    value,
    region,
    externalProjectType,
    lang = null
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    const row = temp[variant][type][index];
    if (!row?.navigationPopup) {
      row.navigationPopup = {
        enabled: false,
        header: { en: "", de: "", fr: "", it: "" },
        subtitle: { en: "", de: "", fr: "", it: "" },
        confirmButtonText: { en: "", de: "", fr: "", it: "" },
        cancelButtonText: { en: "", de: "", fr: "", it: "" }
      };
    }

    if (field === "enabled") {
      row.navigationPopup.enabled = value;
    } else if (lang) {
      if (!row.navigationPopup[field]) {
        row.navigationPopup[field] = { en: "", de: "", fr: "", it: "" };
      }
      row.navigationPopup[field][lang] = value;
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const selectProfessionType = (
    type,
    variant,
    index,
    professionType,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].professionType = professionType;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleUseOrganizationStrengthsListInAptitudeCloud = (
    type,
    variant,
    index,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index].useOrganizationStrengthsList) {
      temp[variant][type][index].useOrganizationStrengthsList = false;
    }

    temp[variant][type][index].useOrganizationStrengthsList =
      !temp[variant][type][index].useOrganizationStrengthsList;

    temp[variant][type][index].useOrganizationRolesList = false;
    temp[variant][type][index].useOrganizationProfessionsList = false;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleUseOrganizationRolesListInAptitudeCloud = (
    type,
    variant,
    index,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index].useOrganizationRolesList) {
      temp[variant][type][index].useOrganizationRolesList = false;
    }

    temp[variant][type][index].useOrganizationRolesList =
      !temp[variant][type][index].useOrganizationRolesList;

    temp[variant][type][index].useOrganizationStrengthsList = false;
    temp[variant][type][index].useOrganizationProfessionsList = false;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleUseOrganizationProfessionsListInAptitudeCloud = (
    type,
    variant,
    index,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index].useOrganizationProfessionsList) {
      temp[variant][type][index].useOrganizationProfessionsList = false;
    }

    temp[variant][type][index].useOrganizationProfessionsList =
      !temp[variant][type][index].useOrganizationProfessionsList;

    temp[variant][type][index].useOrganizationStrengthsList = false;
    temp[variant][type][index].useOrganizationRolesList = false;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleShowAsListInAptitudeCloud = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].showAsList = !temp[variant][type][index].showAsList;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const addAptitudeCloudOption = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].aptitudeCloudOptions) {
      temp[variant][type][index].aptitudeCloudOptions = [];
    }
    temp[variant][type][index].aptitudeCloudOptions.push({ en: "", de: "", fr: "", it: "" });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const deleteAptitudeCloudOption = (
    type,
    variant,
    index,
    optionIndex,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].aptitudeCloudOptions.splice(optionIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateAptitudeCloudOption = (
    type,
    variant,
    index,
    optionIndex,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].aptitudeCloudOptions[optionIndex][lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  // 3 functions for add, delete, update customVideoOptions
  const addCustomVideoOption = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    if (!temp[variant][type][index].customVideoOptions) {
      temp[variant][type][index].customVideoOptions = [];
    }
    temp[variant][type][index].customVideoOptions.push({ en: "", de: "", fr: "", it: "" });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const deleteCustomVideoOption = (
    type,
    variant,
    index,
    optionIndex,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].customVideoOptions.splice(optionIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const updateCustomVideoOption = (
    type,
    variant,
    index,
    optionIndex,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].customVideoOptions[optionIndex][lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleClassProjectTypeEnable = (variant, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant].enabled = !temp[variant].enabled;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleProjectLoginButton = (variant, region, externalProjectType, field) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][field] = !temp[variant][field];

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const setExternalProjectId = (variant, value) => {
    const temp = { ...externalProjectTypes };
    temp[variant].projectId = value;
    setExternalProjectTypes(temp);
  };

  const toggleVipClassPageEnabled = (variant) => {
    const temp = { ...externalProjectTypes };
    temp[variant].vipClassPageEnabled = !temp[variant].vipClassPageEnabled;
    setExternalProjectTypes(temp);
  };

  const addVipClassDemoTanId = (variant, tanId) => {
    const id = String(tanId ?? "").trim();
    if (!id) return;
    const temp = { ...externalProjectTypes };
    if (!Array.isArray(temp[variant].vipClassDemoTanIds)) temp[variant].vipClassDemoTanIds = [];
    if (temp[variant].vipClassDemoTanIds.includes(id)) return;
    temp[variant].vipClassDemoTanIds = [...temp[variant].vipClassDemoTanIds, id];
    setExternalProjectTypes(temp);
  };

  const removeVipClassDemoTanId = (variant, index) => {
    const temp = { ...externalProjectTypes };
    if (!Array.isArray(temp[variant].vipClassDemoTanIds)) return;
    temp[variant].vipClassDemoTanIds = temp[variant].vipClassDemoTanIds.filter(
      (_, i) => i !== index
    );
    setExternalProjectTypes(temp);
  };

  const toggleSendEmailAfterCompletion = (
    variant,
    region,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }
    temp[variant][configKey].sendEmailAfterCompletion =
      !temp[variant][configKey].sendEmailAfterCompletion;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const setEmailTemplateId = (
    variant,
    region,
    templateId,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }
    temp[variant][configKey].emailTemplateId = templateId;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const setProjectGroup = (variant, id) => {
    const temp = { ...externalProjectTypes };
    temp[variant].projectGroupId = id;
    setExternalProjectTypes(temp);
  };

  const addClassStudentComponent = (type, variant, componentType, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    let toAdd = _.cloneDeep({ ...classProjectTypesDefault })?.default?.[type]?.find(
      (element) => element.id === componentType
    );

    if (componentType === "aptitudeWordCloud") toAdd = { ...toAdd, aptitudeId: uuidv4() };

    if (componentType === "professionRolesImageCloud")
      toAdd = { ...toAdd, professionRolesImageCloudId: uuidv4() };

    if (componentType === "professionSelection") toAdd = { ...toAdd, professionSelId: uuidv4() };

    if (componentType === "profileVideoSelection")
      toAdd = { ...toAdd, profileVideoSelId: uuidv4() };

    if (componentType === "videosPlayer") toAdd = { ...toAdd, aptitudeId: uuidv4() };

    if (componentType === "singleQuestionBox") toAdd = { ...toAdd, singleQuestionBoxId: uuidv4() };

    if (componentType === "dataTable") toAdd = { ...toAdd, dataTableId: uuidv4() };

    if (componentType === "attitudesComponent")
      toAdd = { ...toAdd, attitudesComponentId: uuidv4() };

    if (!temp[variant]) temp[variant] = {};

    if (!temp[variant]?.[type]) temp[variant][type] = [];

    temp[variant][type].push(toAdd);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const deleteClassStudentComponent = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type].splice(index, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleShowClassStudentComponentInPdf = (
    type,
    variant,
    index,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].hideInPdf = !temp[variant][type][index].hideInPdf;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const duplicateClassProject = (variant, region) => {
    const temp =
      !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    const toCopy = _.cloneDeep({ ...temp[variant] });
    const nextVariant = `variant_${uuidv4()}`;

    temp[nextVariant] = toCopy;
    // update title to mention it is a copy
    temp[nextVariant].title.en += " (Copy)";

    if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const deleteClassProject = (variant, region) => {
    const temp =
      !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (variant !== "default" && temp[variant] && Object.keys(temp).length > 1) {
      delete temp[variant];
    }

    if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const setOrderOfClassProject = (variant, region, orderNumber) => {
    const temp =
      !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    const toCopy = _.cloneDeep({ ...temp[variant] });
    temp[variant] = toCopy;
    temp[variant].order = Number(orderNumber) || 0;

    if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const duplicateClassProjectTypeForRegion = (existingProjectObject, regionId, oldRegionId) => {
    const copy = _.cloneDeep({ ...existingProjectObject });
    const temp = { ...regionalProjectTypes };
    temp[regionId] = copy;

    if (oldRegionId) {
      delete temp[oldRegionId];
    }

    setRegionalProjectTypes((prev) => ({ ...temp }));
  };

  const deleteRegionalClassProjectType = (region) => {
    const temp = { ...regionalProjectTypes };
    delete temp[region];

    setRegionalProjectTypes(temp);
  };

  const onDragEndClassStudentSetup = (
    type = "classPage",
    variant,
    result,
    region,
    externalProjectType
  ) => {
    if (!result.destination) return;

    try {
      const temp = externalProjectType
        ? { ...externalProjectTypes }
        : !region || region === "default"
          ? { ...classProjectTypes }
          : { ...regionalProjectTypes }?.[region];
      const items = [...temp[variant][type]];
      const [removed] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, removed);
      temp[variant][type] = items;

      if (externalProjectType) setExternalProjectTypes(temp);
      else if (!region || region === "default") setClassProjectTypes(temp);
      else
        setRegionalProjectTypes({
          ...regionalProjectTypes,
          [region]: temp
        });
    } catch (error) {
      console.error("Drag and Drop error", error);
    }
  };

  const updateListLength = (type, variant, index, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].listLength = Number(value) || 0;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else
      setRegionalProjectTypes({
        ...regionalProjectTypes,
        [region]: temp
      });
  };

  const toggleReplacePreviousSelections = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];
    temp[variant][type][index].replacePreviousSelections =
      !temp[variant][type][index].replacePreviousSelections;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const addTableColumn = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].columns.push({ en: "", de: "", fr: "", it: "" });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateTableColumn = (
    type,
    variant,
    index,
    columnIndex,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].columns[columnIndex] = {
      ...temp[variant][type][index].columns[columnIndex],
      [lang]: value
    };

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const deleteTableColumn = (type, variant, index, columnIndex, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].columns.splice(columnIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateSubInputField = (type, variant, index, lang, value, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index].subInputField) {
      temp[variant][type][index].subInputField = { en: "", de: "", fr: "", it: "" };
    }

    temp[variant][type][index].subInputField[lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  // Attitudes list management functions
  const addAttitudeListItem = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index].attitudesList) {
      temp[variant][type][index].attitudesList = [];
    }
    temp[variant][type][index].attitudesList.push({
      title: { en: "", de: "", fr: "", it: "" },
      description: { en: "", de: "", fr: "", it: "" }
    });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateAttitudeListItem = (
    type,
    variant,
    index,
    itemIndex,
    field,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].attitudesList[itemIndex][field][lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const deleteAttitudeListItem = (type, variant, index, itemIndex, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].attitudesList.splice(itemIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateAttitudeComponentField = (
    type,
    variant,
    index,
    field,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index][field]) {
      temp[variant][type][index][field] = { en: "", de: "", fr: "", it: "" };
    }
    temp[variant][type][index][field][lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateStudentRandomInputsNumber = (
    type,
    variant,
    index,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].numberOfInputs = Number(value) || 0;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateStudentRandomInputsField = (
    type,
    variant,
    index,
    field,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant][type][index][field] && lang) {
      temp[variant][type][index][field] = { en: "", de: "", fr: "", it: "" };
    }

    if (lang) temp[variant][type][index][field][lang] = value;
    else temp[variant][type][index][field] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const addStudentRandomInputsOption = (type, variant, index, region, externalProjectType) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].options.push({ en: "", de: "", fr: "", it: "" });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const removeStudentRandomInputsOption = (
    type,
    variant,
    index,
    optionIndex,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].options.splice(optionIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateStudentRandomInputsOption = (
    type,
    variant,
    index,
    optionIndex,
    lang,
    value,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    temp[variant][type][index].options[optionIndex][lang] = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  // Image upload functions for classMapSelections
  const updateMapImage = (
    type,
    variant,
    index,
    imageField,
    lang,
    imageUrl,
    region,
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!lang) {
      temp[variant][type][index][imageField] = imageUrl;
    } else {
      if (!temp[variant][type][index][imageField]) {
        temp[variant][type][index][imageField] = { en: "", de: "", fr: "", it: "" };
      }
      temp[variant][type][index][imageField][lang] = imageUrl;
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  // VIP Configuration functions
  const setCustomVIPProductId = (
    variant,
    region,
    value,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }
    temp[variant][configKey].customVIPProductId = Number(value) || 0;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const setVIPTestUrlPath = (
    variant,
    region,
    value,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }

    temp[variant][configKey].vipTestUrlPath = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const setVIPServerUrl = (
    variant,
    region,
    value,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }

    temp[variant][configKey].serverUrl = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const updateProjectResources = (
    variant,
    region,
    key,
    value,
    lang = null,
    externalProjectType = false
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp[variant].resources) {
      temp[variant].resources = {
        demoVideo: { url: "", html: { en: "", de: "", fr: "", it: "" } },
        projectFiles: [
          {
            url: "",
            type: "",
            title: { en: "", de: "", fr: "", it: "" },
            colors: { text: "", background: "" }
          }
        ]
      };
    }

    if (lang) {
      _.set(temp[variant].resources[key], lang, value);
    } else {
      _.set(temp[variant].resources, key, value);
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const setVIPConfigProfileSetupType = (
    variant,
    region,
    value,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }
    temp[variant][configKey].profileSetupType = value;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const toggleParentsCustomVipTest = (
    variant,
    region,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }

    temp[variant][configKey].parentsCustomVipTest = !temp[variant][configKey]?.parentsCustomVipTest;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const toggleVipConfigPage = (
    variant,
    region,
    propertyName,
    stepPageIndex,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }

    if (temp[variant][configKey]?.stepPages?.[stepPageIndex]) {
      temp[variant][configKey].stepPages[stepPageIndex]["enabled"] =
        !temp[variant][configKey].stepPages[stepPageIndex]["enabled"];
    } else {
      temp[variant][configKey][propertyName] = {
        ...temp[variant][configKey][propertyName],
        enabled: !temp[variant][configKey][propertyName]?.enabled
      };
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const addVipConfigStepPage = (variant, region, externalProjectType, configKey = "vipConfig") => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }
    if (!temp[variant][configKey].stepPages) {
      temp[variant][configKey].stepPages = [];
    }

    temp[variant][configKey].stepPages.push({
      // Default step page structure
      components: []
    });

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const removeVipConfigStepPage = (
    variant,
    region,
    stepPageIndex,
    configKey = "vipConfig",
    externalProjectType
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }
    if (!temp[variant][configKey].stepPages) {
      temp[variant][configKey].stepPages = [];
    }

    temp[variant][configKey].stepPages.splice(stepPageIndex, 1);

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const storeVipConfigThemeInProjectType = (
    variant,
    region,
    savedValues,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (!temp?.[variant]?.[configKey]) {
      temp[variant][configKey] = {};
    }

    temp[variant][configKey].themeSetup = savedValues;

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });

    saveTeacherAdminSetup();
  };

  const addVipConfigPageComponent = (
    variant,
    region,
    pageId,
    component = {},
    stepPageIndex = null,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (stepPageIndex !== null) {
      if (!temp?.[variant]?.[configKey]) {
        temp[variant][configKey] = {};
      }
      if (!temp[variant][configKey].stepPages) {
        temp[variant][configKey].stepPages = [];
      }

      if (!temp[variant][configKey].stepPages?.[stepPageIndex]?.components) {
        temp[variant][configKey].stepPages[stepPageIndex].components = [];
      }

      temp[variant][configKey].stepPages[stepPageIndex].components.push(component);
    } else {
      if (!temp?.[variant]?.[configKey]) {
        temp[variant][configKey] = {};
      }
      if (!temp[variant][configKey][pageId]) {
        temp[variant][configKey][pageId] = {};
      }

      if (!temp[variant][configKey]?.[pageId]?.components) {
        temp[variant][configKey][pageId].components = [];
      }

      temp[variant][configKey][pageId].components.push(component);
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const deleteVipConfigPageComponent = (
    variant,
    region,
    pageId,
    componentIndex,
    stepPageIndex = null,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (stepPageIndex !== null) {
      temp?.[variant]?.[configKey]?.stepPages?.[stepPageIndex]?.components?.splice(
        componentIndex,
        1
      );
    } else {
      temp?.[variant]?.[configKey]?.[pageId]?.components?.splice(componentIndex, 1);
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const onDragEndVipConfigPageComponent = (
    variant,
    region,
    pageId,
    stepPageIndex,
    result,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    if (!result.destination) return;

    try {
      const temp = externalProjectType
        ? { ...externalProjectTypes }
        : !region || region === "default"
          ? { ...classProjectTypes }
          : { ...regionalProjectTypes }?.[region];

      let items;

      if (stepPageIndex !== null) {
        items = [...(temp?.[variant]?.[configKey]?.stepPages?.[stepPageIndex]?.components || [])];
      } else {
        items = [...(temp?.[variant]?.[configKey]?.[pageId]?.components || [])];
      }

      const [removed] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, removed);

      if (stepPageIndex !== null) {
        if (!temp?.[variant]?.[configKey]) temp[variant][configKey] = {};
        if (!temp[variant][configKey].stepPages) temp[variant][configKey].stepPages = [];
        if (!temp[variant][configKey].stepPages?.[stepPageIndex])
          temp[variant][configKey].stepPages[stepPageIndex] = { components: [] };
        temp[variant][configKey].stepPages[stepPageIndex].components = items;
      } else {
        if (!temp?.[variant]?.[configKey]) temp[variant][configKey] = {};
        if (!temp[variant][configKey][pageId]) temp[variant][configKey][pageId] = {};
        temp[variant][configKey][pageId].components = items;
      }

      if (externalProjectType) setExternalProjectTypes(temp);
      else if (!region || region === "default") setClassProjectTypes(temp);
      else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
    } catch (error) {
      console.error("Drag and Drop error", error);
    }
  };

  const updateKeyValueInVipConfigPageComponent = (
    variant,
    region,
    pageId,
    componentIndex,
    updatedComponent,
    stepPageIndex = null,
    externalProjectType,
    configKey = "vipConfig"
  ) => {
    const temp = externalProjectType
      ? { ...externalProjectTypes }
      : !region || region === "default"
        ? { ...classProjectTypes }
        : { ...regionalProjectTypes }?.[region];

    if (stepPageIndex !== null) {
      if (!temp?.[variant]?.[configKey]) temp[variant][configKey] = {};
      if (!temp[variant][configKey].stepPages) temp[variant][configKey].stepPages = [];
      if (!temp[variant][configKey].stepPages?.[stepPageIndex])
        temp[variant][configKey].stepPages[stepPageIndex] = { components: [] };
      if (!temp[variant][configKey].stepPages[stepPageIndex].components)
        temp[variant][configKey].stepPages[stepPageIndex].components = [];
      temp[variant][configKey].stepPages[stepPageIndex].components[componentIndex] = {
        ...updatedComponent
      };
    } else {
      if (!temp?.[variant]?.[configKey]) temp[variant][configKey] = {};
      if (!temp[variant][configKey][pageId]) temp[variant][configKey][pageId] = {};
      if (!temp[variant][configKey][pageId].components)
        temp[variant][configKey][pageId].components = [];
      temp[variant][configKey][pageId].components[componentIndex] = { ...updatedComponent };
    }

    if (externalProjectType) setExternalProjectTypes(temp);
    else if (!region || region === "default") setClassProjectTypes(temp);
    else setRegionalProjectTypes({ ...regionalProjectTypes, [region]: temp });
  };

  const saveTeacherAdminSetup = async () => {
    try {
      setLoading("Saving");

      const body = {
        maintenanceMode,
        enableModerators,
        parentsVIPProductId,
        vipTestLink,
        serverBaseUrl,
        registrationPath,
        productId,
        registrationForm,
        classServices,
        classDownloadsConfig,
        studentPortalSetup,
        teachersPortalFeatures,
        adminPanelTabs,
        classProjectTypes,
        regionalProjectTypes,
        externalProjectTypes
      };
      await updateTeachersPortal(currentService._id, currentService.masterOrganization, body);
      toast.success(currentService?.name + " Setup saved successfully");
    } catch (error) {
      console.log(error?.response?.data?.message);
      toast.error("Failed to save Teacher Admin Setup because " + error?.response?.data?.message);
      console.error("Save Teacher Admin Setup error", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch regions and system admin groups & profession maps etc
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getRegions(org?._id);
        setRegions(data);

        const { data: userGroupsData } = await getSystemAdminUserGroups(org?._id);
        const temp = [];
        userGroupsData.forEach((group) => {
          if (!group?.groups) return;

          group.groups.forEach((subGroup) => {
            temp.push(subGroup);
          });
        });
        setSystemAdminGroups(temp);

        const { data: professionMapsData } = await getProfessionMaps(org?._id);
        setProfessionMaps(professionMapsData);

        const { data: emailTemplatesData } = await getEmailTemplates(org?._id);
        setEmailTemplates(emailTemplatesData);

        const { data: regionalEventsData } = await getRegionalEvents(org?._id);
        setRegionalEvents(regionalEventsData);

        const { data: accessibleToOptions } = await getProfessionsAccessibleToOptions(org?._id);

        try {
          setProfessionTypes(accessibleToOptions);
        } catch (error) {
          console.log("Error profession types list", error);
        }
      } catch (error) {
        console.error("Fetch regions error", error);
      }
    })();
  }, []);

  // Fetch Teacher Admin Setup
  useEffect(() => {
    (async () => {
      try {
        setLoading("Loading from DB");
        const { data } = await getTeachersPortal(
          currentService._id,
          currentService.masterOrganization
        );
        if (data.maintenanceMode) setMaintenanceMode(data?.maintenanceMode);
        setVipTestLink(data?.vipTestLink);
        setServerBaseUrl(data?.serverBaseUrl);
        setRegistrationPath(data?.registrationPath);
        setProductId(data?.productId);
        setEnableModerators(data?.enableModerators);
        setParentsVIPProductId(data?.parentsVIPProductId);

        if (data?.registrationForm?.length > 0) {
          const copy = [...registrationForm];
          data.registrationForm.forEach((element, index) => {
            copy[index].enabled = element.enabled;
            copy[index].required = element.required;
          });
          setRegistrationForm([...copy]);
        }

        if (Object?.keys(data?.classServices)?.length > 0) {
          const temp = { ...classServices };
          Object.keys(data?.classServices).forEach((key) => {
            if (temp[key]) {
              temp[key].enabled = data.classServices[key]?.enabled;
              temp[key].enabledForHistoryTab = data.classServices[key]?.enabledForHistoryTab;
              if (data.classServices[key]?.order !== undefined) {
                temp[key].order = data.classServices[key].order;
              }
            }
          });
          setClassServices(temp);
        }

        if (Object?.keys(data?.classDownloadsConfig)?.length > 0) {
          const temp = { ...classDownloadsConfig };
          Object.keys(data?.classDownloadsConfig).forEach((key) => {
            if (temp.hasOwnProperty(key)) {
              temp[key] = data.classDownloadsConfig[key];
            }
          });
          setClassDownloadsConfig(temp);
        }

        if (data?.studentPortalSetup) {
          if (Object?.keys(data?.studentPortalSetup)?.length > 0) {
            const temp = { ...studentPortalSetup };
            Object.keys(data?.studentPortalSetup).forEach((key) => {
              if (temp.hasOwnProperty(key)) {
                temp[key].enabled = data.studentPortalSetup[key]?.enabled;
              }
            });
            setStudentPortalSetup(temp);
          }
        }

        if (data?.teachersPortalFeatures) {
          const temp = { ...teachersPortalFeaturesDefault };
          if (data.teachersPortalFeatures.downloadsLinkLocation) {
            temp.downloadsLinkLocation = {
              ...temp.downloadsLinkLocation,
              ...data.teachersPortalFeatures.downloadsLinkLocation
            };
          }
          if (typeof data.teachersPortalFeatures.showNumberOfParticipants === "boolean") {
            temp.showNumberOfParticipants = data.teachersPortalFeatures.showNumberOfParticipants;
          }
          if (typeof data.teachersPortalFeatures.enableHistoryTab === "boolean") {
            temp.enableHistoryTab = data.teachersPortalFeatures.enableHistoryTab;
          }
          if (typeof data.teachersPortalFeatures.historyTabDate === "string") {
            temp.historyTabDate = data.teachersPortalFeatures.historyTabDate;
          }
          if (data.teachersPortalFeatures.portalTheme) {
            temp.portalTheme = {
              ...temp.portalTheme,
              ...data.teachersPortalFeatures.portalTheme
            };
          }
          setTeachersPortalFeatures(temp);
        }

        if (data?.adminPanelTabs?.default?.length > 0) {
          const temp = {};
          Object.keys(data?.adminPanelTabs).forEach((key) => {
            temp[key] = _.cloneDeep([...adminPanelTabsDefault.default]);
            data.adminPanelTabs[key].forEach((element, index) => {
              temp[key][index].enabled = element.enabled;
              if (element.buttonLinks) {
                temp[key][index].buttonLinks = element.buttonLinks;
              }
              if (element.subOptions) {
                element.subOptions.forEach((subOption, subIndex) => {
                  if (subOption?.enabled)
                    temp[key][index].subOptions[subIndex].enabled = subOption?.enabled;
                  if (subOption?.value)
                    temp[key][index].subOptions[subIndex].value = subOption?.value;
                });
              }
            });
          });
          setAdminPanelTabs({ ...temp });
          setDuplicatedRegions(
            Object.keys(data?.adminPanelTabs)?.filter((key) => key !== "default")
          );
        }

        if (data?.classProjectTypes?.default?.classPage?.length > 0) {
          const temp = {};
          Object.keys(data?.classProjectTypes).forEach((variant) => {
            temp[variant] = _.cloneDeep({ ...data?.classProjectTypes[variant] });
          });
          setClassProjectTypes({ ...temp });
        }

        if (data?.regionalProjectTypes) {
          const temp = {};
          Object.keys(data?.regionalProjectTypes).forEach((region) => {
            temp[region] = _.cloneDeep({ ...data?.regionalProjectTypes[region] });
          });
          setRegionalProjectTypes({ ...temp });
        }

        if (data?.externalProjectTypes) {
          const temp = {};
          Object.keys(data?.externalProjectTypes).forEach((variant) => {
            temp[variant] = _.cloneDeep({ ...data?.externalProjectTypes[variant] });
          });
          setExternalProjectTypes({ ...temp });
        }
      } catch (error) {
        console.error(error, "This setup does not exist " + error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // console.log(classProjectTypes?.default);
  // console.log(externalProjectTypes);

  return (
    <>
      <button
        className="flex gap-3 items-center w-[fit-content] flex-wrap mb-10"
        onClick={setShowComponent}>
        <span className="w-8 h-8">
          <ArrowBackIcon />
        </span>
        <h2 className="text-xl leading-10 font-bold">{title ?? "mission future School"}</h2>
      </button>

      <div className="pl-8">
        {/* Portal Maintenance mode */}
        <PortalMaintenanceMode
          currentService={currentService}
          org={org}
          maintenanceMode={maintenanceMode}
          toggleMaintenanceMode={toggleMaintenanceMode}
          toggleMaintenanceLogo={toggleMaintenanceLogo}
          updateMaintenceModeHTML={updateMaintenceModeHTML}
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
        />

        {/* Class downloads configuration inputs  */}
        <PortalConfigVariables
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          enableModerators={enableModerators}
          setEnableModerators={setEnableModerators}
          vipTestLink={vipTestLink}
          setVipTestLink={setVipTestLink}
          serverBaseUrl={serverBaseUrl}
          setServerBaseUrl={setServerBaseUrl}
          registrationPath={registrationPath}
          setRegistrationPath={setRegistrationPath}
          productId={productId}
          setProductId={setProductId}
          parentsVIPProductId={parentsVIPProductId}
          setParentsVIPProductId={setParentsVIPProductId}
          classDownloadsConfig={classDownloadsConfig}
          setClassDownloadsConfig={setClassDownloadsConfig}
        />

        {/* Teacher registration page config */}
        <TeacherRegistrationPageConfig
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          registrationForm={registrationForm}
          toggleEnableFormField={toggleEnableFormField}
          toggleRequiredFormField={toggleRequiredFormField}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
        />

        {/* Class page table features */}
        <ClassPageFeatures
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          classServices={classServices}
          toggleEnableClassService={toggleEnableClassService}
          toggleEnableClassServiceForHistoryTab={toggleEnableClassServiceForHistoryTab}
          onDragEndClassService={onDragEndClassService}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
        />

        {/* Student portal setup */}
        <StudentPortalSetup
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          studentPortalSetup={studentPortalSetup}
          toggleStudentPortalService={toggleStudentPortalService}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
        />

        {/* Teachers Portal Features */}
        <TeachersPortalFeaturesSetup
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          teachersPortalFeatures={teachersPortalFeatures}
          updateTeachersPortalFeatures={updateTeachersPortalFeatures}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
        />

        {/* Teachers Portal Configurations -- Variants */}
        <TeachersAdminPortalSetup
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          adminPanelTabs={adminPanelTabs}
          regions={regions}
          duplicateForRegion={duplicateForRegion}
          toggleAdminPanelTabs={toggleAdminPanelTabs}
          addNewButtonLink={addNewButtonLink}
          deleteButtonLink={deleteButtonLink}
          updateButtonLinkInfo={updateButtonLinkInfo}
          updateTextInputValue={updateTextInputValue}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
          deleteRegion={deleteRegion}
          duplicatedRegions={duplicatedRegions}
        />

        {/* Class Project Types -- Variants */}
        <ClassProjectTypesSetup
          currentService={currentService}
          org={org}
          languages={org?.languages}
          serverBaseUrl={serverBaseUrl}
          displayToggle={displayToggle}
          setDisplayToggle={setDisplayToggle}
          classProjectTypes={classProjectTypes}
          setClassProjectTypes={setClassProjectTypes}
          regions={regions}
          professionMaps={professionMaps}
          systemAdminGroups={systemAdminGroups}
          professionTypes={professionTypes}
          regionalProjectTypes={regionalProjectTypes}
          externalProjectTypes={externalProjectTypes}
          setExternalProjectTypes={setExternalProjectTypes}
          deleteRegionalClassProjectType={deleteRegionalClassProjectType}
          deleteClassProject={deleteClassProject}
          duplicateClassProject={duplicateClassProject}
          saveTeacherAdminSetup={saveTeacherAdminSetup}
          toggleClassProjectTypeEnable={toggleClassProjectTypeEnable}
          toggleProjectLoginButton={toggleProjectLoginButton}
          setExternalProjectId={setExternalProjectId}
          toggleVipClassPageEnabled={toggleVipClassPageEnabled}
          addVipClassDemoTanId={addVipClassDemoTanId}
          removeVipClassDemoTanId={removeVipClassDemoTanId}
          toggleShowClassStudentComponentInPdf={toggleShowClassStudentComponentInPdf}
          setOrderOfClassProject={setOrderOfClassProject}
          updateListLength={updateListLength}
          toggleReplacePreviousSelections={toggleReplacePreviousSelections}
          addTableColumn={addTableColumn}
          updateTableColumn={updateTableColumn}
          deleteTableColumn={deleteTableColumn}
          addAttitudeListItem={addAttitudeListItem}
          updateAttitudeListItem={updateAttitudeListItem}
          deleteAttitudeListItem={deleteAttitudeListItem}
          updateAttitudeComponentField={updateAttitudeComponentField}
          updateStudentRandomInputsNumber={updateStudentRandomInputsNumber}
          updateStudentRandomInputsField={updateStudentRandomInputsField}
          addStudentRandomInputsOption={addStudentRandomInputsOption}
          removeStudentRandomInputsOption={removeStudentRandomInputsOption}
          updateStudentRandomInputsOption={updateStudentRandomInputsOption}
          updateMapImage={updateMapImage}
          updateClassProjectTitle={updateClassProjectTitle}
          duplicateClassProjectTypeForRegion={duplicateClassProjectTypeForRegion}
          addClassStudentComponent={addClassStudentComponent}
          deleteClassStudentComponent={deleteClassStudentComponent}
          onDragEndClassStudentSetup={onDragEndClassStudentSetup}
          updateBackgroundHeading={updateBackgroundHeading}
          updateForegroundHeading={updateForegroundHeading}
          updateSubText={updateSubText}
          updateButtonText={updateButtonText}
          updateDownloadType={updateDownloadType}
          updateProjectFor={updateProjectFor}
          updateHTMLBox={updateHTMLBox}
          updateVideosPlayerTitle={updateVideosPlayerTitle}
          updateVideosPlayerUrl={updateVideosPlayerUrl}
          addNewVideoInVideosPlayer={addNewVideoInVideosPlayer}
          deleteVideoInVideosPlayer={deleteVideoInVideosPlayer}
          toggleVideoPlayerAptitudeCloud={toggleVideoPlayerAptitudeCloud}
          selectProjectTypeRegionalLink={selectProjectTypeRegionalLink}
          selectUsersGroup={selectUsersGroup}
          discoverChampionCustomUrl={discoverChampionCustomUrl}
          selectProfessionType={selectProfessionType}
          toggleUseOrganizationStrengthsListInAptitudeCloud={
            toggleUseOrganizationStrengthsListInAptitudeCloud
          }
          toggleUseOrganizationRolesListInAptitudeCloud={
            toggleUseOrganizationRolesListInAptitudeCloud
          }
          toggleUseOrganizationProfessionsListInAptitudeCloud={
            toggleUseOrganizationProfessionsListInAptitudeCloud
          }
          toggleShowAsListInAptitudeCloud={toggleShowAsListInAptitudeCloud}
          updateAptitudeCloudOption={updateAptitudeCloudOption}
          addAptitudeCloudOption={addAptitudeCloudOption}
          deleteAptitudeCloudOption={deleteAptitudeCloudOption}
          addCustomVideoOption={addCustomVideoOption}
          deleteCustomVideoOption={deleteCustomVideoOption}
          setRegionalProjectTypes={setRegionalProjectTypes}
          updateCustomVideoOption={updateCustomVideoOption}
          setCustomVIPProductId={setCustomVIPProductId}
          setVIPTestUrlPath={setVIPTestUrlPath}
          setVIPServerUrl={setVIPServerUrl}
          setVIPConfigProfileSetupType={setVIPConfigProfileSetupType}
          toggleVipConfigPage={toggleVipConfigPage}
          addVipConfigStepPage={addVipConfigStepPage}
          removeVipConfigStepPage={removeVipConfigStepPage}
          storeVipConfigThemeInProjectType={storeVipConfigThemeInProjectType}
          addVipConfigPageComponent={addVipConfigPageComponent}
          deleteVipConfigPageComponent={deleteVipConfigPageComponent}
          onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
          updateKeyValueInVipConfigPageComponent={updateKeyValueInVipConfigPageComponent}
          updateTotalBadgesToShow={updateTotalBadgesToShow}
          selectStudentAttributesToMatch={selectStudentAttributesToMatch}
          regionalEvents={regionalEvents}
          setRegionalEventProfileType={setRegionalEventProfileType}
          selectRegionalEvent={selectRegionalEvent}
          updateSimilarProfileBadgesNavigationPopupField={
            updateSimilarProfileBadgesNavigationPopupField
          }
          emailTemplates={emailTemplates}
          toggleSendEmailAfterCompletion={toggleSendEmailAfterCompletion}
          setEmailTemplateId={setEmailTemplateId}
          updateSubInputField={updateSubInputField}
          updateInfoPopupField={updateInfoPopupField}
          setProjectGroup={setProjectGroup}
          toggleParentsCustomVipTest={toggleParentsCustomVipTest}
          updateProjectResources={updateProjectResources}
        />
      </div>

      <div className="fixed bottom-4 right-4">
        <Button.AdminPanelButton
          type="button"
          isSave={true}
          loading={false}
          bgColor="#515151"
          textColor="#FFFFFF"
          color="white"
          extraClasses="hover:scale-105 transition-all"
          onClick={saveTeacherAdminSetup}>
          <span className="flex gap-3 items-center">
            <span className="w-6 h-6 ">
              <FaSave />
            </span>
            <span>Save Full Setup</span>
          </span>
        </Button.AdminPanelButton>
      </div>

      {loading && <FullScreenLoader text={loading} />}
    </>
  );
};

export default TeacherAdminSetup;

const PortalMaintenanceMode = ({
  currentService,
  org,
  maintenanceMode,
  toggleMaintenanceMode,
  toggleMaintenanceLogo,
  updateMaintenceModeHTML,
  displayToggle,
  setDisplayToggle,
  saveTeacherAdminSetup
}) => {
  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4">
      <p
        className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            maintenanceMode: !displayToggle.maintenanceMode
          });
        }}>
        Teachers Portal Maintenance Mode
      </p>

      <div className={`my-4 ${displayToggle.maintenanceMode ? "block" : "hidden"}`}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold">Enable mainteance mode</p>
          <div className="flex gap-3 items-center">
            <ToggleComponent
              handleCompToggle={() => toggleMaintenanceMode()}
              isToggle={maintenanceMode?.enabled}
            />
          </div>
        </div>
        {maintenanceMode?.enabled && (
          <div className="flex items-center justify-between mb-4">
            <p className="font-bold">Show logo</p>
            <div className="flex gap-3 items-center">
              <ToggleComponent
                handleCompToggle={() => toggleMaintenanceLogo()}
                isToggle={maintenanceMode?.showLogo}
              />
            </div>
          </div>
        )}

        {maintenanceMode?.enabled && (
          <div className="my-4">
            <TextArea
              className={"w-full"}
              inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
              labelClassName={"text-sm font-normal text-black"}
              label={"Maintenance Mode HTML"}
              placeholder={`<div>Site is under construction</div>`}
              value={maintenanceMode?.html}
              onChange={(e) => {
                updateMaintenceModeHTML(e.target.value);
              }}
            />
            <p className="text-xs text-gray-400 mt-2">
              This is an approximate preview of this HTML
            </p>
            <div className="mt-2 border border-gray-400 rounded p-2">
              {maintenanceMode?.showLogo && (
                <img
                  src={
                    process.env.REACT_APP_AWS_URL +
                    (currentService?.type === "mission-future-school"
                      ? org?.schoolLogo
                      : org?.teamLogo)
                  }
                  className="h-40 w-auto block mx-auto my-4"
                />
              )}
              <div dangerouslySetInnerHTML={{ __html: maintenanceMode?.html }} />
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button.AdminPanelButton
            type="submit"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={saveTeacherAdminSetup}>
            Save
          </Button.AdminPanelButton>
        </div>
      </div>
    </div>
  );
};

const PortalConfigVariables = ({
  displayToggle,
  setDisplayToggle,
  enableModerators,
  setEnableModerators,
  vipTestLink,
  setVipTestLink,
  serverBaseUrl,
  setServerBaseUrl,
  registrationPath,
  setRegistrationPath,
  productId,
  setProductId,
  parentsVIPProductId,
  setParentsVIPProductId,
  classDownloadsConfig,
  setClassDownloadsConfig
}) => {
  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4">
      <p
        className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            classDownloadsConfig: !displayToggle.classDownloadsConfig
          });
        }}>
        Portal Config Variables
      </p>
      <div className={`${displayToggle.classDownloadsConfig ? "block" : "hidden"}`}>
        <div className="flex items-center justify-between my-4">
          <p className="font-bold">Enable Moderators</p>
          <div className="flex gap-3 items-center">
            <ToggleComponent
              handleCompToggle={() => setEnableModerators(!enableModerators)}
              isToggle={enableModerators}
            />
          </div>
        </div>
        <div className="my-4">
          <Input
            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
            label="VIP Test"
            placeholder={"www.viptestlink.co.za"}
            name="vipTestLink"
            type="url"
            defaultValue={vipTestLink}
            onChange={(e) => setVipTestLink(e.target.value)}
          />
        </div>
        <div className="my-4">
          <Input
            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
            label="Server Base URL"
            placeholder={"www.teacherregistration.co.za"}
            name="serverBaseUrl"
            type="url"
            defaultValue={serverBaseUrl}
            onChange={(e) => setServerBaseUrl(e.target.value)}
          />
        </div>
        <div className="my-4">
          <Input
            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
            label="Registration path"
            placeholder={"missionfuture-cc"}
            name="registrationPath"
            type="text"
            defaultValue={registrationPath}
            onChange={(e) => setRegistrationPath(e.target.value)}
          />
        </div>
        <div className="my-4">
          <Input
            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
            label="Product ID"
            placeholder={"42"}
            name="productId"
            type="text"
            defaultValue={productId}
            onChange={(e) => setProductId(Number(e.target.value))}
          />
        </div>
        <div className="my-4">
          <Input
            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
            label="Parents VIP Product ID"
            placeholder={"42"}
            name="parentsVIPProductId"
            type="text"
            defaultValue={parentsVIPProductId}
            onChange={(e) => setParentsVIPProductId(Number(e.target.value))}
          />
        </div>

        {Object.keys(classDownloadsConfig).map((key, index) => {
          return (
            <div key={index} className="my-4">
              <Input
                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                label={key}
                name={key}
                type="text"
                defaultValue={classDownloadsConfig[key]}
                onChange={(e) =>
                  setClassDownloadsConfig({ ...classDownloadsConfig, [key]: e.target.value })
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TeacherRegistrationPageConfig = ({
  displayToggle,
  setDisplayToggle,
  registrationForm,
  toggleEnableFormField,
  toggleRequiredFormField,
  saveTeacherAdminSetup
}) => {
  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4">
      <p
        className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            registrationForm: !displayToggle.registrationForm
          });
        }}>
        Teachers Registration Form
      </p>

      <div className={`my-4 ${displayToggle.registrationForm ? "block" : "hidden"}`}>
        {registrationForm.map((element, index) => (
          <div key={index} className="flex items-center justify-between mb-4">
            <p className="font-bold">{element.label}</p>
            <div className="flex gap-3 items-center">
              {element.allowDisable && (
                <>
                  <ToggleComponent
                    handleCompToggle={() => toggleEnableFormField(index)}
                    isToggle={element.enabled}
                  />
                  <button
                    className="w-5 h-5 cursor-pointer"
                    onClick={() => toggleRequiredFormField(index)}>
                    <StarIcon fill={element?.required ? "red" : ""} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}

        <div className="mt-5 flex justify-end">
          <Button.AdminPanelButton
            type="submit"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={saveTeacherAdminSetup}>
            Save
          </Button.AdminPanelButton>
        </div>
      </div>
    </div>
  );
};

const ClassPageFeatures = ({
  displayToggle,
  setDisplayToggle,
  classServices,
  toggleEnableClassService,
  toggleEnableClassServiceForHistoryTab,
  onDragEndClassService,
  saveTeacherAdminSetup
}) => {
  const sortedKeys = Object.keys(classServices).sort(
    (a, b) => classServices[a].order - classServices[b].order
  );

  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4">
      <p
        className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            classServices: !displayToggle.classServices
          });
        }}>
        Class Page Features
      </p>

      <div className={`my-4 ${displayToggle.classServices ? "block" : "hidden"}`}>
        <DragDropContext onDragEnd={onDragEndClassService}>
          <Droppable droppableId="classServices">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {sortedKeys.map((key, index) => {
                  const service = classServices[key];
                  return (
                    <Draggable key={key} draggableId={key} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`flex items-center justify-between mb-4 p-2 rounded ${
                            snapshot.isDragging ? "bg-gray-200" : ""
                          }`}>
                          <div className="flex items-center gap-2">
                            <span className="cursor-grab text-gray-400">⋮⋮</span>
                            <p className="font-bold">{service.label}</p>
                          </div>
                          <div className="flex gap-3 items-center">
                            <ToggleComponent
                              handleCompToggle={() => toggleEnableClassService(key)}
                              isToggle={service.enabled}
                            />
                            <div className="flex items-center gap-2 border border-gray-400 rounded-md p-2">
                              <p className="font-bold">Show in history tab</p>
                              <ToggleComponent
                                handleCompToggle={() => toggleEnableClassServiceForHistoryTab(key)}
                                isToggle={service.enabledForHistoryTab}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="mt-5 flex justify-end">
          <Button.AdminPanelButton
            type="submit"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={saveTeacherAdminSetup}>
            Save
          </Button.AdminPanelButton>
        </div>
      </div>
    </div>
  );
};

const StudentPortalSetup = ({
  displayToggle,
  setDisplayToggle,
  studentPortalSetup,
  toggleStudentPortalService,
  saveTeacherAdminSetup
}) => {
  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4">
      <p
        className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            studentPortalSetup: !displayToggle.studentPortalSetup
          });
        }}>
        Student Portal Setup
      </p>

      <div className={`my-4 ${displayToggle.studentPortalSetup ? "block" : "hidden"}`}>
        {Object.keys(studentPortalSetup).map((key, index) => {
          const service = studentPortalSetup[key];
          return (
            <div key={index} className="flex items-center justify-between mb-4">
              <p className="font-bold">{service.label}</p>
              <div className="flex gap-3 items-center">
                <button className="w-4 h-4 cursor-pointer">{editIconPencil}</button>
                <ToggleComponent
                  handleCompToggle={() => toggleStudentPortalService(key)}
                  isToggle={service.enabled}
                />
              </div>
            </div>
          );
        })}

        <div className="mt-5 flex justify-end">
          <Button.AdminPanelButton
            type="submit"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={saveTeacherAdminSetup}>
            Save
          </Button.AdminPanelButton>
        </div>
      </div>
    </div>
  );
};

const TeachersPortalFeaturesSetup = ({
  displayToggle,
  setDisplayToggle,
  teachersPortalFeatures,
  updateTeachersPortalFeatures,
  saveTeacherAdminSetup
}) => {
  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4">
      <p
        className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            teachersPortalFeatures: !displayToggle.teachersPortalFeatures
          });
        }}>
        Teachers Portal Features
      </p>

      <div className={`my-4 ${displayToggle.teachersPortalFeatures ? "block" : "hidden"}`}>
        {/* Downloads Link Location */}
        <div className="mb-6">
          <p className="font-bold mb-3">Downloads Link Location</p>
          <div className="ml-4 space-y-3">
            <div className="flex items-center justify-between">
              <p>Sidebar Default</p>
              <ToggleComponent
                handleCompToggle={() =>
                  updateTeachersPortalFeatures(
                    "downloadsLinkLocation",
                    !teachersPortalFeatures.downloadsLinkLocation.sidebarDefault,
                    "sidebarDefault",
                    true
                  )
                }
                isToggle={teachersPortalFeatures.downloadsLinkLocation.sidebarDefault}
              />
            </div>
            <div className="flex items-center justify-between">
              <p>Sidebar Bottom</p>
              <ToggleComponent
                handleCompToggle={() =>
                  updateTeachersPortalFeatures(
                    "downloadsLinkLocation",
                    !teachersPortalFeatures.downloadsLinkLocation.sidebarBottom,
                    "sidebarBottom",
                    true
                  )
                }
                isToggle={teachersPortalFeatures.downloadsLinkLocation.sidebarBottom}
              />
            </div>
          </div>
        </div>

        {/* Show Number of Participants */}
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold">Show Number of Participants</p>
          <ToggleComponent
            handleCompToggle={() =>
              updateTeachersPortalFeatures(
                "showNumberOfParticipants",
                !teachersPortalFeatures.showNumberOfParticipants
              )
            }
            isToggle={teachersPortalFeatures.showNumberOfParticipants}
          />
        </div>

        {/* History Tab */}
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold">Enable History Tab</p>
          <ToggleComponent
            handleCompToggle={() =>
              updateTeachersPortalFeatures(
                "enableHistoryTab",
                !teachersPortalFeatures.enableHistoryTab
              )
            }
            isToggle={teachersPortalFeatures.enableHistoryTab}
          />
        </div>
        <div className="mb-4">
          <Input
            className="w-full"
            inputClassName="rounded-full bg-[#F6F5F5]"
            labelClassName="text-sm font-normal text-black"
            label="History Tab Date"
            placeholder="YYYY-MM-DD"
            type="date"
            value={teachersPortalFeatures.historyTabDate || ""}
            onChange={(e) => updateTeachersPortalFeatures("historyTabDate", e.target.value)}
          />
        </div>

        {/* Portal Theme */}
        <div className="mb-4">
          <p className="font-bold mb-3">Portal Theme</p>
          <div className="ml-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Input
                className="w-full"
                inputClassName="rounded-full bg-[#F6F5F5]"
                labelClassName="text-sm font-normal text-black"
                label="Projects Table First Column Text Color"
                placeholder="#000"
                value={teachersPortalFeatures.portalTheme.projectsTableFirstColumnTextColor}
                onChange={(e) =>
                  updateTeachersPortalFeatures(
                    "portalTheme",
                    e.target.value,
                    "projectsTableFirstColumnTextColor"
                  )
                }
              />
              <span
                className="absolute right-2 top-[65%] -translate-y-1/2 h-8 w-8 rounded-full border border-gray-300"
                style={{
                  background: teachersPortalFeatures.portalTheme.projectsTableFirstColumnTextColor
                }}
              />
            </div>
            <div className="relative">
              <Input
                className="w-full"
                inputClassName="rounded-full bg-[#F6F5F5]"
                labelClassName="text-sm font-normal text-black"
                label="Projects Table Headers Text Color"
                placeholder="#000"
                value={teachersPortalFeatures.portalTheme.projectsTableHeadersTextColor}
                onChange={(e) =>
                  updateTeachersPortalFeatures(
                    "portalTheme",
                    e.target.value,
                    "projectsTableHeadersTextColor"
                  )
                }
              />
              <span
                className="absolute right-2 top-[65%] -translate-y-1/2 h-8 w-8 rounded-full border border-gray-300"
                style={{
                  background: teachersPortalFeatures.portalTheme.projectsTableHeadersTextColor
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button.AdminPanelButton
            type="submit"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={saveTeacherAdminSetup}>
            Save
          </Button.AdminPanelButton>
        </div>
      </div>
    </div>
  );
};

const TeachersAdminPortalSetup = ({
  displayToggle,
  setDisplayToggle,
  adminPanelTabs,
  regions,
  duplicateForRegion,
  toggleAdminPanelTabs,
  addNewButtonLink,
  deleteButtonLink,
  updateButtonLinkInfo,
  updateTextInputValue,
  saveTeacherAdminSetup,
  deleteRegion,
  duplicatedRegions
}) => {
  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4 bg-gray-50">
      <p
        className="font-bold text-2xl mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            teacherAdminPortalSetups: !displayToggle.teacherAdminPortalSetups
          });
        }}>
        Teachers Admin Portal setups
      </p>
      {displayToggle.teacherAdminPortalSetups &&
        Object.keys(adminPanelTabs).map((key) => {
          const tabDetails = adminPanelTabs[key];
          const tabRegion = regions.find((region) => region._id === key);
          const tabRegionName =
            key === "default"
              ? "Default"
              : tabRegion?.name_en ||
                tabRegion?.name_de ||
                tabRegion?.name_fr ||
                tabRegion?.name_it;

          return (
            <div key={key} className="my-10 border-[1px] border-[#606060] rounded-md p-4 bg-white">
              <p
                className="w-full font-bold text-lg mb-2 cursor-pointer select-none"
                onClick={() => {
                  setDisplayToggle({
                    ...displayToggle,
                    [`teacherPortal-${key}`]: !displayToggle[`teacherPortal-${key}`]
                  });
                }}>
                ({tabRegionName}) mission future School
              </p>

              <div className={`${displayToggle[`teacherPortal-${key}`] ? "block" : "hidden"}`}>
                {key !== "default" && (
                  <div className="my-4">
                    <label className="block font-bold mb-1" htmlFor={key}>
                      Region-specific
                    </label>
                    <InputSelect
                      name={key}
                      defaultValue={{ value: key, label: tabRegionName }}
                      options={regions
                        .map((region) => {
                          if (region._id === key || duplicatedRegions.includes(region._id))
                            return null;
                          return {
                            value: region._id,
                            label:
                              region.name_en || region.name_de || region.name_fr || region.name_it
                          };
                        })
                        .filter((value) => value !== null)}
                      onChange={(e) => duplicateForRegion(key, e.value, true)}
                    />
                  </div>
                )}
                <>
                  {tabDetails.map((element, index) => {
                    return (
                      <div key={index} className="mb-4">
                        <div className="flex items-center justify-between mb-4">
                          <p className="font-bold text-lg">{element.label}</p>
                          <div className="flex gap-3 items-center">
                            <button className="w-4 h-4 cursor-pointer">{editIconPencil}</button>
                            {element.allowDisable && (
                              <ToggleComponent
                                handleCompToggle={() => toggleAdminPanelTabs(index, key)}
                                isToggle={element.enabled}
                              />
                            )}
                          </div>
                        </div>
                        {element.buttonLinks && (
                          <>
                            <div className="flex flex-row justify-between items-center mb-4">
                              <p className="font-semibold">Button Links</p>
                              <button onClick={() => addNewButtonLink(index, key)}>
                                <PlusIcon />
                              </button>
                            </div>
                            <div className="flex flex-col gap-4">
                              <>
                                {element.buttonLinks.map((button, buttonIndex) => {
                                  return (
                                    <div
                                      key={buttonIndex}
                                      className="relative flex flex-col gap-2 w-full border p-2 pt-5 rounded">
                                      <button
                                        className="absolute right-0 top-0 p-2"
                                        onClick={() => deleteButtonLink(index, key, buttonIndex)}>
                                        <MinusSquare />
                                      </button>
                                      <div className="flex flex-row gap-1">
                                        {["en", "de", "fr", "it"].map((lang) => {
                                          return (
                                            <Input
                                              key={lang}
                                              className={"w-full"}
                                              inputClassName={
                                                "rounded-full border-none bg-[#F6F5F5]"
                                              }
                                              labelClassName={"text-sm font-normal text-[#606060]"}
                                              label={lang.toUpperCase() + " Title"}
                                              placeholder={lang}
                                              value={button.title[lang]}
                                              onChange={(e) => {
                                                const temp = { ...button };
                                                temp.title[lang] = e.target.value;
                                                updateButtonLinkInfo(index, key, buttonIndex, temp);
                                              }}
                                            />
                                          );
                                        })}
                                      </div>
                                      <Input
                                        className={"w-full"}
                                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                        labelClassName={"text-sm font-normal text-[#606060]"}
                                        label="Link"
                                        placeholder={"https://google.com"}
                                        value={button.link}
                                        onChange={(e) => {
                                          const temp = { ...button };
                                          temp.link = e.target.value;
                                          updateButtonLinkInfo(index, key, buttonIndex, temp);
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </>
                            </div>
                          </>
                        )}
                        {element.subOptions && (
                          <div className="px-8">
                            {element.subOptions.map((subOption, subIndex) => {
                              if (subOption.type === "text-input")
                                return (
                                  <Input
                                    key={subIndex}
                                    className={"w-full"}
                                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                    labelClassName={"font-bold"}
                                    label={subOption.label}
                                    placeholder={subOption.placeholder ?? ""}
                                    value={subOption.value}
                                    onChange={(e) => {
                                      updateTextInputValue(index, key, subIndex, e.target.value);
                                    }}
                                  />
                                );
                              return (
                                <div
                                  key={subIndex}
                                  className="flex items-center justify-between mb-4">
                                  <p className="font-bold">{subOption.label}</p>
                                  <div className="flex gap-3 items-center">
                                    <button className="w-4 h-4 cursor-pointer">
                                      {editIconPencil}
                                    </button>
                                    {subOption.allowDisable && (
                                      <ToggleComponent
                                        handleCompToggle={() =>
                                          toggleAdminPanelTabs(index, key, subIndex)
                                        }
                                        isToggle={subOption.enabled}
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
                <div className="mt-5 flex justify-end gap-3">
                  <Button.AdminPanelButton
                    type="button"
                    isSave={true}
                    loading={false}
                    bgColor="#515151"
                    textColor="#FFFFFF"
                    color="white"
                    onClick={saveTeacherAdminSetup}>
                    Save
                  </Button.AdminPanelButton>
                  {duplicatedRegions.length !== regions.length && (
                    <Button.AdminPanelButton
                      type="button"
                      isSave={true}
                      loading={false}
                      bgColor="#515151"
                      textColor="#FFFFFF"
                      color="white"
                      onClick={() => duplicateForRegion(key)}>
                      Duplicate
                    </Button.AdminPanelButton>
                  )}
                  {/* Delete region */}
                  {key !== "default" && (
                    <Button.AdminPanelButton
                      type="button"
                      isSave={true}
                      loading={false}
                      bgColor="#515151"
                      textColor="#FFFFFF"
                      color="white"
                      onClick={() => deleteRegion(key)}>
                      Delete
                    </Button.AdminPanelButton>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

const ClassProjectTypesSetup = ({
  org,
  currentService,
  serverBaseUrl,
  displayToggle,
  setDisplayToggle,
  classProjectTypes,
  setClassProjectTypes,
  regions,
  professionMaps,
  systemAdminGroups,
  professionTypes,
  regionalProjectTypes,
  externalProjectTypes,
  setExternalProjectTypes,
  deleteRegionalClassProjectType,
  deleteClassProject,
  duplicateClassProject,
  saveTeacherAdminSetup,
  toggleClassProjectTypeEnable,
  toggleProjectLoginButton,
  setExternalProjectId,
  toggleVipClassPageEnabled,
  addVipClassDemoTanId,
  removeVipClassDemoTanId,
  toggleShowClassStudentComponentInPdf,
  setOrderOfClassProject,
  updateListLength,
  toggleReplacePreviousSelections,
  addTableColumn,
  updateTableColumn,
  deleteTableColumn,
  addAttitudeListItem,
  updateAttitudeListItem,
  deleteAttitudeListItem,
  updateAttitudeComponentField,
  updateStudentRandomInputsNumber,
  updateStudentRandomInputsField,
  addStudentRandomInputsOption,
  removeStudentRandomInputsOption,
  updateStudentRandomInputsOption,
  updateMapImage,
  updateClassProjectTitle,
  duplicateClassProjectTypeForRegion,
  addClassStudentComponent,
  deleteClassStudentComponent,
  onDragEndClassStudentSetup,
  updateBackgroundHeading,
  updateForegroundHeading,
  updateSubText,
  updateButtonText,
  updateHTMLBox,
  updateVideosPlayerTitle,
  updateVideosPlayerUrl,
  addNewVideoInVideosPlayer,
  deleteVideoInVideosPlayer,
  toggleVideoPlayerAptitudeCloud,
  selectProjectTypeRegionalLink,
  selectUsersGroup,
  discoverChampionCustomUrl,
  selectProfessionType,
  toggleUseOrganizationStrengthsListInAptitudeCloud,
  toggleUseOrganizationRolesListInAptitudeCloud,
  toggleUseOrganizationProfessionsListInAptitudeCloud,
  toggleShowAsListInAptitudeCloud,
  updateAptitudeCloudOption,
  addAptitudeCloudOption,
  deleteAptitudeCloudOption,
  addCustomVideoOption,
  deleteCustomVideoOption,
  updateCustomVideoOption,
  setRegionalProjectTypes,
  setCustomVIPProductId,
  setVIPTestUrlPath,
  setVIPConfigProfileSetupType,
  toggleParentsCustomVipTest,
  toggleVipConfigPage,
  addVipConfigStepPage,
  removeVipConfigStepPage,
  storeVipConfigThemeInProjectType,
  addVipConfigPageComponent,
  deleteVipConfigPageComponent,
  onDragEndVipConfigPageComponent,
  updateKeyValueInVipConfigPageComponent,
  setVIPServerUrl,
  updateTotalBadgesToShow,
  selectStudentAttributesToMatch,
  regionalEvents,
  setRegionalEventProfileType,
  selectRegionalEvent,
  updateSimilarProfileBadgesNavigationPopupField,
  emailTemplates,
  toggleSendEmailAfterCompletion,
  setEmailTemplateId,
  updateSubInputField,
  updateInfoPopupField,
  updateProjectFor,
  setProjectGroup,
  updateProjectResources,
  updateDownloadType,
  languages
}) => {
  return (
    <>
      <div className="my-10 border-[1px] border-[#606060] rounded-md p-4 bg-gray-50">
        <p
          className="font-bold text-2xl mb-2 cursor-pointer select-none"
          onClick={() => {
            setDisplayToggle({
              ...displayToggle,
              classProjectTypesDisplay: !displayToggle.classProjectTypesDisplay
            });
          }}>
          Project types setups
        </p>

        {displayToggle.classProjectTypesDisplay && (
          <>
            <ClassProjectSetup
              languages={languages}
              {...{
                currentService,
                serverBaseUrl,
                org,
                classProjectTypes,
                setClassProjectTypes,
                regions,
                professionMaps,
                systemAdminGroups,
                professionTypes,
                regionalProjectTypes,
                displayToggle,
                setDisplayToggle,
                updateClassProjectTitle,
                duplicateClassProjectTypeForRegion,
                addClassStudentComponent,
                deleteClassStudentComponent,
                onDragEndClassStudentSetup,
                updateBackgroundHeading,
                updateForegroundHeading,
                updateSubText,
                updateButtonText,
                updateHTMLBox,
                updateVideosPlayerTitle,
                updateVideosPlayerUrl,
                addNewVideoInVideosPlayer,
                deleteVideoInVideosPlayer,
                toggleVideoPlayerAptitudeCloud,
                selectProjectTypeRegionalLink,
                selectUsersGroup,
                discoverChampionCustomUrl,
                selectProfessionType,
                toggleUseOrganizationStrengthsListInAptitudeCloud,
                toggleUseOrganizationRolesListInAptitudeCloud,
                toggleUseOrganizationProfessionsListInAptitudeCloud,
                toggleShowAsListInAptitudeCloud,
                updateAptitudeCloudOption,
                addAptitudeCloudOption,
                deleteAptitudeCloudOption,
                addCustomVideoOption,
                deleteCustomVideoOption,
                updateCustomVideoOption,
                deleteRegionalClassProjectType,
                deleteClassProject,
                duplicateClassProject,
                saveTeacherAdminSetup,
                toggleClassProjectTypeEnable,
                toggleProjectLoginButton,
                setExternalProjectId,
                toggleVipClassPageEnabled,
                addVipClassDemoTanId,
                removeVipClassDemoTanId,
                toggleShowClassStudentComponentInPdf,
                setOrderOfClassProject,
                updateListLength,
                toggleReplacePreviousSelections,
                addTableColumn,
                updateTableColumn,
                deleteTableColumn,
                addAttitudeListItem,
                updateAttitudeListItem,
                deleteAttitudeListItem,
                updateMapImage,
                updateAttitudeComponentField,
                updateStudentRandomInputsNumber,
                updateStudentRandomInputsField,
                addStudentRandomInputsOption,
                removeStudentRandomInputsOption,
                updateStudentRandomInputsOption,
                setCustomVIPProductId,
                setVIPTestUrlPath,
                setVIPConfigProfileSetupType,
                toggleParentsCustomVipTest,
                toggleVipConfigPage,
                addVipConfigStepPage,
                removeVipConfigStepPage,
                storeVipConfigThemeInProjectType,
                addVipConfigPageComponent,
                deleteVipConfigPageComponent,
                onDragEndVipConfigPageComponent,
                updateKeyValueInVipConfigPageComponent,
                setVIPServerUrl,
                updateTotalBadgesToShow,
                selectStudentAttributesToMatch,
                regionalEvents,
                setRegionalEventProfileType,
                selectRegionalEvent,
                updateSimilarProfileBadgesNavigationPopupField,
                updateSubInputField,
                updateInfoPopupField,
                updateProjectResources,
                updateDownloadType,
                emailTemplates,
                toggleSendEmailAfterCompletion,
                setEmailTemplateId
              }}
            />

            {/* Regional Projecs types variants */}
            {Object.keys(regionalProjectTypes).map((key) => {
              if (!regionalProjectTypes?.[key]) return null;

              return (
                <ClassProjectSetup
                  key={key}
                  languages={languages}
                  {...{
                    currentService,
                    serverBaseUrl,
                    org,
                    region: key,
                    classProjectTypes: regionalProjectTypes?.[key],
                    setClassProjectTypes: (updated) =>
                      setRegionalProjectTypes({
                        ...regionalProjectTypes,
                        [key]: updated
                      }),
                    regions,
                    professionMaps,
                    systemAdminGroups,
                    professionTypes,
                    regionalProjectTypes,
                    displayToggle,
                    setDisplayToggle,
                    updateClassProjectTitle,
                    duplicateClassProjectTypeForRegion,
                    addClassStudentComponent,
                    deleteClassStudentComponent,
                    onDragEndClassStudentSetup,
                    updateBackgroundHeading,
                    updateForegroundHeading,
                    updateSubText,
                    updateButtonText,
                    updateHTMLBox,
                    updateVideosPlayerTitle,
                    updateVideosPlayerUrl,
                    addNewVideoInVideosPlayer,
                    deleteVideoInVideosPlayer,
                    toggleVideoPlayerAptitudeCloud,
                    selectProjectTypeRegionalLink,
                    selectUsersGroup,
                    discoverChampionCustomUrl,
                    selectProfessionType,
                    toggleUseOrganizationStrengthsListInAptitudeCloud,
                    toggleUseOrganizationRolesListInAptitudeCloud,
                    toggleUseOrganizationProfessionsListInAptitudeCloud,
                    toggleShowAsListInAptitudeCloud,
                    updateAptitudeCloudOption,
                    addAptitudeCloudOption,
                    deleteAptitudeCloudOption,
                    addCustomVideoOption,
                    deleteCustomVideoOption,
                    updateCustomVideoOption,
                    deleteRegionalClassProjectType,
                    deleteClassProject,
                    duplicateClassProject,
                    saveTeacherAdminSetup,
                    toggleClassProjectTypeEnable,
                    toggleProjectLoginButton,
                    setExternalProjectId,
                    toggleVipClassPageEnabled,
                    addVipClassDemoTanId,
                    removeVipClassDemoTanId,
                    toggleShowClassStudentComponentInPdf,
                    setOrderOfClassProject,
                    updateListLength,
                    toggleReplacePreviousSelections,
                    addTableColumn,
                    updateTableColumn,
                    deleteTableColumn,
                    addAttitudeListItem,
                    updateAttitudeListItem,
                    deleteAttitudeListItem,
                    updateMapImage,
                    updateAttitudeComponentField,
                    updateStudentRandomInputsNumber,
                    updateStudentRandomInputsField,
                    addStudentRandomInputsOption,
                    removeStudentRandomInputsOption,
                    updateStudentRandomInputsOption,
                    setCustomVIPProductId,
                    setVIPTestUrlPath,
                    setVIPConfigProfileSetupType,
                    toggleParentsCustomVipTest,
                    toggleVipConfigPage,
                    addVipConfigStepPage,
                    removeVipConfigStepPage,
                    storeVipConfigThemeInProjectType,
                    addVipConfigPageComponent,
                    deleteVipConfigPageComponent,
                    onDragEndVipConfigPageComponent,
                    updateKeyValueInVipConfigPageComponent,
                    setVIPServerUrl,
                    updateTotalBadgesToShow,
                    selectStudentAttributesToMatch,
                    regionalEvents,
                    setRegionalEventProfileType,
                    selectRegionalEvent,
                    updateSimilarProfileBadgesNavigationPopupField,
                    updateSubInputField,
                    updateInfoPopupField,
                    updateProjectFor,
                    updateProjectResources,
                    updateDownloadType,
                    emailTemplates,
                    toggleSendEmailAfterCompletion,
                    setEmailTemplateId
                  }}
                />
              );
            })}
          </>
        )}
      </div>

      {/* External Project Types */}
      <div className="my-10 border-[1px] border-[#606060] rounded-md p-4 bg-[#FF9E441F]">
        <p
          className="font-bold text-2xl mb-2 cursor-pointer select-none"
          onClick={() => {
            setDisplayToggle({
              ...displayToggle,
              externalProjectTypesDisplay: !displayToggle.externalProjectTypesDisplay
            });
          }}>
          External project types
        </p>

        {displayToggle.externalProjectTypesDisplay && (
          <ClassProjectSetup
            languages={languages}
            {...{
              org,
              currentService,
              serverBaseUrl,
              externalProjectType: true,
              classProjectTypes: externalProjectTypes,
              setClassProjectTypes: setExternalProjectTypes,
              regions,
              professionMaps,
              systemAdminGroups,
              professionTypes,
              emailTemplates,
              toggleSendEmailAfterCompletion,
              setEmailTemplateId,
              externalProjectTypes,
              displayToggle,
              setDisplayToggle,
              updateClassProjectTitle,
              duplicateClassProjectTypeForRegion,
              addClassStudentComponent,
              deleteClassStudentComponent,
              onDragEndClassStudentSetup,
              updateBackgroundHeading,
              updateForegroundHeading,
              updateSubText,
              updateButtonText,
              updateHTMLBox,
              updateVideosPlayerTitle,
              updateVideosPlayerUrl,
              addNewVideoInVideosPlayer,
              deleteVideoInVideosPlayer,
              toggleVideoPlayerAptitudeCloud,
              selectProjectTypeRegionalLink,
              selectUsersGroup,
              discoverChampionCustomUrl,
              selectProfessionType,
              toggleUseOrganizationStrengthsListInAptitudeCloud,
              toggleUseOrganizationRolesListInAptitudeCloud,
              toggleUseOrganizationProfessionsListInAptitudeCloud,
              toggleShowAsListInAptitudeCloud,
              updateAptitudeCloudOption,
              addAptitudeCloudOption,
              deleteAptitudeCloudOption,
              addCustomVideoOption,
              deleteCustomVideoOption,
              updateCustomVideoOption,
              deleteRegionalClassProjectType,
              deleteClassProject,
              duplicateClassProject,
              saveTeacherAdminSetup,
              toggleClassProjectTypeEnable,
              toggleProjectLoginButton,
              setExternalProjectId,
              toggleVipClassPageEnabled,
              addVipClassDemoTanId,
              removeVipClassDemoTanId,
              toggleShowClassStudentComponentInPdf,
              setOrderOfClassProject,
              updateListLength,
              toggleReplacePreviousSelections,
              addTableColumn,
              updateTableColumn,
              deleteTableColumn,
              setCustomVIPProductId,
              updateStudentRandomInputsNumber,
              updateStudentRandomInputsField,
              addStudentRandomInputsOption,
              removeStudentRandomInputsOption,
              updateStudentRandomInputsOption,
              setVIPTestUrlPath,
              setVIPConfigProfileSetupType,
              toggleParentsCustomVipTest,
              toggleVipConfigPage,
              addVipConfigStepPage,
              removeVipConfigStepPage,
              storeVipConfigThemeInProjectType,
              addVipConfigPageComponent,
              deleteVipConfigPageComponent,
              onDragEndVipConfigPageComponent,
              updateKeyValueInVipConfigPageComponent,
              setVIPServerUrl,
              updateTotalBadgesToShow,
              selectStudentAttributesToMatch,
              regionalEvents,
              setRegionalEventProfileType,
              selectRegionalEvent,
              updateSimilarProfileBadgesNavigationPopupField,
              updateSubInputField,
              updateInfoPopupField,
              updateProjectFor,
              setProjectGroup,
              updateProjectResources,
              updateDownloadType
            }}
          />
        )}
      </div>
    </>
  );
};
